import { useEffect, useState } from "react";
import { api, fileUrl, thumbUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import Sidebar from "../components/Sidebar";
import PageTopBar from "../components/PageTopBar";
import RetryImage from "../components/RetryImage";
import { useConfirm } from "../components/ConfirmDialog";
import { Heart, Copy, ChevronLeft, ChevronRight, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 24;

export default function LibraryPage() {
    const { user: me } = useAuth();
    // Delete is a platform-wide destructive op (nukes storage + chat
    // history + ratings + refs). Restricted to super_admin only — the
    // backend also enforces this, but hiding the button in the UI
    // prevents a confusing 403 for regular users who see the trash
    // icon and can't understand why the click bounces back.
    const canDelete = me?.role === "super_admin";
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState("all"); // all | favorites
    const [loading, setLoading] = useState(true);
    // Locally-hidden IDs for immediate UI feedback while the DELETE request
    // is in flight — prevents the tile from flashing back before `load()`
    // finishes and gives the user the "instant remove" feel they expect.
    const [pendingRemove, setPendingRemove] = useState(() => new Set());
    const confirm = useConfirm();

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const load = (pageNum = page) => {
        setLoading(true);
        const skip = (pageNum - 1) * PAGE_SIZE;
        const params = new URLSearchParams({
            skip: String(skip),
            limit: String(PAGE_SIZE),
        });
        if (filter === "favorites") params.set("favorite", "true");
        api.get(`/library?${params.toString()}`)
            .then((r) => {
                setItems(r.data.items || []);
                setTotal(r.data.total || 0);
            })
            .finally(() => setLoading(false));
    };

    // Reset to page 1 on filter change
    useEffect(() => {
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    useEffect(() => {
        load(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filter]);

    useEffect(() => {
        const onBrand = () => {
            setPage(1);
            load(1);
        };
        window.addEventListener("kc:brand-changed", onBrand);
        return () => window.removeEventListener("kc:brand-changed", onBrand);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const toggleFav = async (id) => {
        try {
            await api.post(`/library/${id}/favorite`);
            load(page);
        } catch {
            /* noop */
        }
    };

    const removeItem = async (it) => {
        const ok = await confirm({
            title: "¿Eliminar esta imagen permanentemente?",
            description:
                "Se borra de la biblioteca, del chat donde se generó y del almacenamiento. Los ratings y referencias que apuntaban a esta imagen también se limpian. Esta acción NO se puede deshacer.",
            confirmLabel: "Sí, eliminar",
            cancelLabel: "Cancelar",
            tone: "destructive",
            kind: "delete",
        });
        if (!ok) return;
        // Optimistic hide — the tile disappears immediately even before
        // the network round-trip completes. Falls back to reloading on
        // success (backend truth), or restoring on error.
        setPendingRemove((prev) => new Set(prev).add(it.id));
        try {
            await api.delete(`/library/${it.id}`);
            toast.success("Imagen eliminada de la biblioteca");
            // Refresh the page — total count changes, pagination may
            // shift, and if we just emptied the last tile of the last
            // page we need to hop back one page.
            const remainingOnPage = items.length - 1;
            if (remainingOnPage === 0 && page > 1) {
                setPage((p) => p - 1);
            } else {
                load(page);
            }
        } catch (e) {
            setPendingRemove((prev) => {
                const next = new Set(prev);
                next.delete(it.id);
                return next;
            });
            toast.error(
                e?.response?.data?.detail || "No se pudo eliminar la imagen",
            );
        }
    };

    return (
        <div className="flex min-h-screen bg-ink-950">
            <Sidebar />
            <main className="flex-1 overflow-y-auto">
                <PageTopBar />
                <div className="max-w-7xl mx-auto px-10 py-14">
                    <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                        <div>
                            <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-2">
                                Archivo
                            </p>
                            <h1 className="font-display text-5xl text-fog-100">
                                Biblioteca de piezas
                            </h1>
                            <p className="text-fog-400 text-sm mt-2 max-w-xl">
                                Todas las imágenes generadas por el estudio.
                                Úsalas como referencia en futuros chats.
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <FilterBtn active={filter === "all"} onClick={() => setFilter("all")} label="Todas" testId="lib-filter-all" />
                            <FilterBtn active={filter === "favorites"} onClick={() => setFilter("favorites")} label="Favoritas" testId="lib-filter-fav" />
                        </div>
                    </div>

                    {loading && <p className="text-fog-400 font-mono text-xs">Cargando…</p>}
                    {!loading && items.length === 0 && (
                        <div className="rounded-2xl border hairline border-dashed border-ink-600 p-16 text-center">
                            <p className="text-fog-400">Aún sin piezas. Genera tu primera imagen.</p>
                        </div>
                    )}

                    {/* Compact grid with lazy-loaded WebP thumbnails for speed. */}
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2.5">
                        {items
                            .filter((it) => !pendingRemove.has(it.id))
                            .map((it) => (
                            <figure
                                key={it.id}
                                data-testid={`library-item-${it.id}`}
                                className="group relative rounded-lg overflow-hidden border hairline border-ink-600 bg-ink-900 aspect-square"
                            >
                                <RetryImage
                                    src={thumbUrl(it.storage_path, 320)}
                                    alt=""
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                                />
                                {it.favorite && (
                                    <span
                                        className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full glass border hairline border-periwinkle-500/60 text-periwinkle-400 flex items-center justify-center"
                                        title="Favorita"
                                    >
                                        <Heart className="w-2.5 h-2.5" fill="currentColor" />
                                    </span>
                                )}
                                <figcaption
                                    className="absolute inset-x-0 bottom-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                    style={{
                                        background:
                                            "linear-gradient(to top, rgba(10,14,42,0.92) 0%, rgba(10,14,42,0.55) 60%, transparent 100%)",
                                    }}
                                >
                                    <p className="text-white text-[11px] line-clamp-2 font-medium leading-tight">
                                        {it.message_prompt}
                                    </p>
                                    <p className="font-mono text-[8px] text-white/70 mt-0.5 uppercase tracking-widest truncate">
                                        {it.width}×{it.height} · {it.model_id?.split("/").pop()}
                                    </p>
                                </figcaption>
                                <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <IconBtn onClick={() => toggleFav(it.id)} testId={`lib-fav-${it.id}`}>
                                        <Heart className="w-3 h-3" fill={it.favorite ? "currentColor" : "none"} />
                                    </IconBtn>
                                    <IconBtn
                                        onClick={async () => {
                                            const text = it.refined_prompt || it.message_prompt || "";
                                            try {
                                                await navigator.clipboard.writeText(text);
                                                toast.success("Prompt copiado");
                                            } catch {
                                                // Permission denied (non-secure origin, headless
                                                // browsers, sandboxed iframes). Don't lie to the
                                                // user with a success toast — surface the real
                                                // outcome so they can fall back to Ctrl+C.
                                                toast.error("No se pudo copiar (permiso del portapapeles)");
                                            }
                                        }}
                                        testId={`lib-copy-${it.id}`}
                                    >
                                        <Copy className="w-3 h-3" />
                                    </IconBtn>
                                    <a
                                        href={fileUrl(it.storage_path)}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`lib-open-${it.id}`}
                                        title="Abrir original"
                                        className="w-6 h-6 rounded-full glass border hairline border-ink-600 text-fog-100 hover:text-periwinkle-500 flex items-center justify-center"
                                    >
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                    {canDelete && (
                                        <IconBtn
                                            onClick={() => removeItem(it)}
                                            testId={`lib-delete-${it.id}`}
                                            title="Eliminar"
                                            danger
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </IconBtn>
                                    )}
                                </div>
                            </figure>
                        ))}
                    </div>

                    {total > 0 && (
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            total={total}
                            onChange={setPage}
                        />
                    )}
                </div>
            </main>
        </div>
    );
}

function Pagination({ page, totalPages, total, onChange }) {
    const from = (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(page * PAGE_SIZE, total);
    return (
        <div
            data-testid="library-pagination"
            className="mt-10 flex items-center justify-between gap-4 flex-wrap"
        >
            <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                {from}–{to} de {total}
            </p>
            <div className="flex items-center gap-2">
                <button
                    data-testid="library-pagination-prev"
                    onClick={() => onChange(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    className="rounded-lg border hairline border-ink-600 text-fog-300 hover:text-periwinkle-500 hover:border-periwinkle-500 w-9 h-9 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Anterior"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-1">
                    {buildPageList(page, totalPages).map((p, idx) =>
                        p === "…" ? (
                            <span
                                key={`dots-${idx}`}
                                className="px-2 text-fog-500 font-mono text-xs"
                            >
                                …
                            </span>
                        ) : (
                            <button
                                key={p}
                                data-testid={`library-pagination-page-${p}`}
                                onClick={() => onChange(p)}
                                className={`min-w-[36px] h-9 px-3 rounded-lg text-xs font-mono transition-colors ${
                                    p === page
                                        ? "bg-periwinkle-500 text-ink-950"
                                        : "border hairline border-ink-600 text-fog-300 hover:text-periwinkle-500 hover:border-periwinkle-500"
                                }`}
                            >
                                {p}
                            </button>
                        ),
                    )}
                </div>
                <button
                    data-testid="library-pagination-next"
                    onClick={() => onChange(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages}
                    className="rounded-lg border hairline border-ink-600 text-fog-300 hover:text-periwinkle-500 hover:border-periwinkle-500 w-9 h-9 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Siguiente"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

function buildPageList(cur, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = new Set([1, total, cur - 1, cur, cur + 1]);
    const sorted = Array.from(pages)
        .filter((p) => p >= 1 && p <= total)
        .sort((a, b) => a - b);
    const out = [];
    sorted.forEach((p, i) => {
        if (i > 0 && p - sorted[i - 1] > 1) out.push("…");
        out.push(p);
    });
    return out;
}

function FilterBtn({ active, onClick, label, testId }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition-all border hairline ${
                active
                    ? "border-periwinkle-500 text-periwinkle-500 bg-ink-800"
                    : "border-ink-600 text-fog-400 hover:text-fog-100"
            }`}
        >
            {label}
        </button>
    );
}

function IconBtn({ children, onClick, testId, title, danger = false }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            title={title}
            className={`w-6 h-6 rounded-full glass border hairline flex items-center justify-center transition-colors ${
                danger
                    ? "border-ink-600 text-fog-100 hover:text-destructive hover:border-destructive"
                    : "border-ink-600 text-fog-100 hover:text-periwinkle-500"
            }`}
        >
            {children}
        </button>
    );
}
